/**
 * Thin client for the admin HTTP API. Every call attaches a fresh
 * Cognito ID token as a bearer token — the HTTP API's JWT authorizer
 * validates it, and the AdminUsers Lambda additionally enforces
 * "admins" group membership before touching any Cognito admin call.
 *
 * Errors are thrown as plain Error instances with the server's error
 * message when available. The admin page renders them verbatim.
 */
import { getIdToken } from './auth';

const BaseUrl = import.meta.env.VITE_API_URL as string;

export interface AdminUser {
  sub: string;
  email: string | null;
  status: string;
  enabled: boolean;
  createdAt: string | null;
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  if (!BaseUrl) {
    throw new Error('VITE_API_URL is not configured');
  }
  const token = await getIdToken();
  if (!token) {
    throw new Error('Not authenticated');
  }

  const resp = await fetch(`${BaseUrl}${path}`, {
    method,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(body ? { 'Content-Type': 'application/json' } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  if (resp.status === 204) {
    return undefined as T;
  }

  const text = await resp.text();
  const payload = text ? (JSON.parse(text) as unknown) : null;

  if (!resp.ok) {
    const msg =
      payload && typeof payload === 'object' && 'error' in payload
        ? String((payload as { error: unknown }).error)
        : `Request failed (${resp.status})`;
    throw new Error(msg);
  }

  return payload as T;
}

export function listUsers(): Promise<{ users: AdminUser[] }> {
  return request('GET', '/admin/users');
}

export function inviteUser(email: string): Promise<AdminUser> {
  return request('POST', '/admin/users', { email });
}

export function enableUser(sub: string): Promise<{ sub: string; enabled: boolean }> {
  return request('POST', `/admin/users/${encodeURIComponent(sub)}/enable`);
}

export function disableUser(sub: string): Promise<{ sub: string; enabled: boolean }> {
  return request('POST', `/admin/users/${encodeURIComponent(sub)}/disable`);
}

export function deleteUser(sub: string): Promise<void> {
  return request('DELETE', `/admin/users/${encodeURIComponent(sub)}`);
}

/**
 * One-shot data migration. Scans the single-table store for rows with
 * the legacy "MAP#" element-partition prefix (renamed to "SCENE#" on
 * 2026-04-21), wipes them, and seeds a default scene index row so the
 * frontend's hardcoded "local-dev" scene has a catalog entry.
 *
 * Idempotent: a second run finds zero MAP# rows and refreshes the
 * default scene's UpdatedAt. Dry-run counts rows but makes no writes.
 */
export interface MigrateScenesResult {
  tableName: string;
  dryRun: boolean;
  deleted: number;
  defaultSceneWritten: boolean;
  defaultSceneId: string;
  defaultCampaignId: string;
  defaultSceneIndexPk: string;
  defaultSceneIndexSk: string;
}

export function migrateScenes(opts: {
  dryRun: boolean;
  defaultSceneId?: string;
  defaultCampaignId?: string;
}): Promise<MigrateScenesResult> {
  return request('POST', '/admin/migrate-scenes', opts);
}
